// externals
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// actions
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as rollToActionCreators from '@makemydeal/dr-dash-store/actions/rollToActionCreators';
import * as rollToSelectors from '@makemydeal/dr-dash-store/selectors/rollToSelectors';

// interstate components
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import type { TabProps } from '@interstate/components/Tabs';
import { Tabs } from '@interstate/components/Tabs';

// custom internal components
import RollToSlideOut from './RollToSlideOut';
import RollToOptionTarget from './rollTo/RollToOptionTarget';
import RollToPaymentOptions from './rollTo/RollToPaymentOptions';
import RollToPaymentProfit from './rollTo/RollToPaymentProfit';

// CSS style components
import { CenteredLoadingIndicator } from '../common/Loading.style';

// utils
import { BALLOON, ExtendedOfferTypeEnum, type CalculationEndpointType } from '@makemydeal/dr-platform-types';
import { ROLL_TO_OPTIONS, ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import { getDisplayOfferType } from '../utils/formatUtils';
import { CALC_ENDPOINTS } from '@makemydeal/dr-dash-store/constants';
import { actionCreators as offerReduxActionCreators } from '@makemydeal/dr-offer-redux';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

const RollToOptionsInterstate = () => {
    const dispatch = useDispatch();
    const activeTab = useSelector(rollToSelectors.getActiveTab);
    const showRollOptions = useSelector(rollToSelectors.getRollToPaymentSlideOutExpanded);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType) as ExtendedOfferTypeEnum;
    const monthlyPayment = useSelector(offerSelectors.getMonthlyPayment);
    const profitAmount = useSelector(offerSelectors.getProfitDetail);
    const isCalculating = useSelector(rollToSelectors.isCalculating);
    const currentCalcEndpoint = useSelector((state: any) =>
        offerReduxSelectors.getCalculationEndpointByOfferType(state, currentOfferType)
    );
    const dealCalcEndpoint = useRef<CalculationEndpointType | undefined>(currentCalcEndpoint);
    const isBalloonInPqMode = currentOfferType === BALLOON && dealCalcEndpoint.current === CALC_ENDPOINTS.programQuotes;
    const targetPayment = useSelector(rollToSelectors.getRollTargetPayment);
    const targetBalanceDue = useSelector(rollToSelectors.getTargetBalanceDue);
    const targetProfit = useSelector(rollToSelectors.getRollTargetProfit);
    const targetCashDue = useSelector(rollToSelectors.getTargetCashDue);

    const currentBalanceDue = useSelector(offerSelectors.getBalanceDueAmountByOffer);
    const currentCashDue = useSelector(offerReduxSelectors.getCashDueFromCustomer);
    const isRollToTabPersistenceEnabled = useSelector(featureToggleSelectors.isRollToTabPersistenceEnabled);
    const isRollToUpqOnFirstCalculationEnabled = useSelector(featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled);
    const isRollToCashDue = useSelector(rollToSelectors.isRollToCashDueApplicable);
    const isRollToBalanceDueOfferType = [
        ExtendedOfferTypeEnum.CASH,
        ExtendedOfferTypeEnum.FINANCE,
        ExtendedOfferTypeEnum.BALLOON
    ].includes(currentOfferType);
    const isEligibleForRollToPayment = [
        ExtendedOfferTypeEnum.LEASE,
        ExtendedOfferTypeEnum.FINANCE,
        ExtendedOfferTypeEnum.BALLOON
    ].includes(currentOfferType);

    // Helper functions for creating tabs
    const createPaymentTab = useCallback((): TabProps | null => {
        if (!isEligibleForRollToPayment) return null;

        return {
            label: ROLL_TO_OPTIONS_LABELS.PAYMENT,
            active: activeTab === ROLL_TO_OPTIONS_LABELS.PAYMENT,
            id: ROLL_TO_OPTIONS_LABELS.PAYMENT,
            component: (
                <RollToOptionTarget
                    key={activeTab}
                    displayProps={{
                        option: ROLL_TO_OPTIONS_LABELS.PAYMENT,
                        currentValue: monthlyPayment,
                        ...(isRollToTabPersistenceEnabled && { targetValue: targetPayment }),
                        instruction: `Adjust Target ${ROLL_TO_OPTIONS_LABELS.PAYMENT} to review your Roll-To Options.`,
                        showDealPulseTooltip: true
                    }}
                    dispatchAction={rollToActionCreators.getRollToPayment({ option: ROLL_TO_OPTIONS.PAYMENT })}
                    clearAction={rollToActionCreators.resetRollToOptions}
                />
            )
        };
    }, [isEligibleForRollToPayment, activeTab, monthlyPayment, targetPayment, isRollToTabPersistenceEnabled]);

    const createBalanceDueTab = useCallback((): TabProps | null => {
        if (!isRollToBalanceDueOfferType) return null;

        return {
            label: ROLL_TO_OPTIONS_LABELS.BALANCE_DUE,
            active: activeTab === ROLL_TO_OPTIONS_LABELS.BALANCE_DUE,
            id: ROLL_TO_OPTIONS_LABELS.BALANCE_DUE,
            component: (
                <RollToOptionTarget
                    key={activeTab}
                    displayProps={{
                        option: ROLL_TO_OPTIONS_LABELS.BALANCE_DUE,
                        currentValue: currentBalanceDue,
                        ...(isRollToTabPersistenceEnabled && { targetValue: targetBalanceDue }),
                        instruction: `Adjust ${ROLL_TO_OPTIONS_LABELS.BALANCE_DUE} to review your Roll-To Options.`
                    }}
                    dispatchAction={rollToActionCreators.getRollToPayment({ option: ROLL_TO_OPTIONS.BALANCE_DUE })}
                    clearAction={rollToActionCreators.resetRollToOptions}
                />
            )
        };
    }, [isRollToBalanceDueOfferType, activeTab, currentBalanceDue, targetBalanceDue, isRollToTabPersistenceEnabled]);

    const createCashDueTab = useCallback((): TabProps | null => {
        if (!isRollToCashDue) return null;

        return {
            label: ROLL_TO_OPTIONS_LABELS.CASH_DUE,
            active: activeTab === ROLL_TO_OPTIONS_LABELS.CASH_DUE,
            id: ROLL_TO_OPTIONS_LABELS.CASH_DUE,
            component: (
                <RollToOptionTarget
                    key={activeTab}
                    displayProps={{
                        option: ROLL_TO_OPTIONS_LABELS.CASH_DUE,
                        currentValue: currentCashDue,
                        ...(isRollToTabPersistenceEnabled && { targetValue: targetCashDue }),
                        instruction: `Adjust ${ROLL_TO_OPTIONS_LABELS.CASH_DUE} to review your Roll-To Options.`
                    }}
                    dispatchAction={rollToActionCreators.getRollToPayment({ option: ROLL_TO_OPTIONS.CASH_DUE })}
                    clearAction={rollToActionCreators.resetRollToOptions}
                />
            )
        };
    }, [isRollToCashDue, activeTab, currentCashDue, targetCashDue, isRollToTabPersistenceEnabled]);

    const createProfiteTab = useCallback((): TabProps | null => {
        return {
            label: ROLL_TO_OPTIONS_LABELS.PROFIT,
            active: activeTab === ROLL_TO_OPTIONS_LABELS.PROFIT,
            id: ROLL_TO_OPTIONS_LABELS.PROFIT,
            component: (
                <RollToOptionTarget
                    key={activeTab}
                    displayProps={{
                        option: ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT,
                        currentValue: profitAmount,
                        ...(isRollToTabPersistenceEnabled && { targetValue: targetProfit }),
                        instruction: `Adjust ${ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT} to review your Roll-To Options.`
                    }}
                    dispatchAction={rollToActionCreators.getRollToPayment({ option: ROLL_TO_OPTIONS.PROFIT })}
                    clearAction={rollToActionCreators.resetRollToOptions}
                />
            )
        };
    }, [activeTab, profitAmount, targetProfit, isRollToTabPersistenceEnabled]);

    // Create tabs array using helper functions
    const rollToOptionsTabs = useMemo(() => {
        const tabs: TabProps[] = [];

        const paymentTab = createPaymentTab();
        if (paymentTab) tabs.push(paymentTab);

        const balanceDueTab = createBalanceDueTab();
        if (balanceDueTab) tabs.push(balanceDueTab);

        const cashDueTab = createCashDueTab();
        if (cashDueTab) tabs.push(cashDueTab);

        const profitTab = createProfiteTab();
        if (profitTab) tabs.push(profitTab);

        return tabs;
    }, [createPaymentTab, createBalanceDueTab, createCashDueTab, createProfiteTab]);

    useEffect(() => {
        const firstActiveTab = rollToOptionsTabs.at(0);
        // The available tabs depend on offer type and, for Cash Due, on the lease payment frequency — all of which
        // can change while the slide out is open. Fall back to the first tab whenever the active one is gone,
        // otherwise Tabs renders with nothing selected and a roll would target a no-longer-applicable option.
        const isActiveTabAvailable = rollToOptionsTabs.some((tab) => tab.id === activeTab);

        if (firstActiveTab && showRollOptions && (!activeTab || !isActiveTabAvailable)) {
            dispatch(rollToActionCreators.setRollToActiveTab(firstActiveTab.id as ROLL_TO_OPTIONS_LABELS));
            if (activeTab && !isRollToTabPersistenceEnabled) {
                dispatch(rollToActionCreators.resetRollToOptions());
            }
        }
        // After opening the slide out move to upq mode if balloon in pq mode
        if (isBalloonInPqMode && showRollOptions && !isRollToUpqOnFirstCalculationEnabled) {
            dispatch(offerReduxActionCreators.setProgramFallbackOverrides());
            dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, currentOfferType));
        }
    }, [activeTab, rollToOptionsTabs, showRollOptions, isBalloonInPqMode, isRollToTabPersistenceEnabled]);

    // Store the current calc status when slide out is closed
    useEffect(() => {
        if (!showRollOptions && dealCalcEndpoint.current !== currentCalcEndpoint) {
            dealCalcEndpoint.current = currentCalcEndpoint;
        }
    }, [showRollOptions, currentCalcEndpoint]);

    const toggleSlideOut = useCallback(() => {
        dispatch(rollToActionCreators.toggleRollToPayment());
        if (isBalloonInPqMode && !isRollToUpqOnFirstCalculationEnabled) {
            dispatch(offerReduxActionCreators.updateCurrentCalculationEndpoint(CALC_ENDPOINTS.programQuotes, currentOfferType));
        }
    }, [dispatch, isBalloonInPqMode, isRollToUpqOnFirstCalculationEnabled, currentOfferType]);

    const handleChangeActiveTab = useCallback(
        (tab: number) => {
            const foundTab = rollToOptionsTabs.find((_, index) => index === tab);

            dispatch(rollToActionCreators.setRollToActiveTab(foundTab!.id as ROLL_TO_OPTIONS_LABELS));
            if (!isRollToTabPersistenceEnabled) {
                dispatch(rollToActionCreators.resetRollToOptions());
            }
        },
        [dispatch, rollToOptionsTabs, isRollToTabPersistenceEnabled]
    );

    if (!showRollOptions) return null;

    return (
        <RollToSlideOut
            show={showRollOptions}
            onHide={toggleSlideOut}
            header={`Roll-To Calculator: ${getDisplayOfferType(currentOfferType)} Deal`}
        >
            <div id="roll-to-options-wrapper" data-testid="roll-options-wrapper">
                {isCalculating && (
                    <CenteredLoadingIndicator
                        data-testid="roll-to-options-loading-indicator"
                        $hideOverlay
                        style={{
                            position: 'absolute',
                            zIndex: 999
                        }}
                    >
                        <LoadingIndicator size="large" />
                    </CenteredLoadingIndicator>
                )}
                <Tabs
                    id="roll-to-options-tabs"
                    sx={{
                        position: 'sticky !important',
                        top: '0',
                        zIndex: '10',
                        backgroundColor: '#FFF'
                    }}
                    tabs={rollToOptionsTabs}
                    onActivate={handleChangeActiveTab}
                />
                <div style={{ marginTop: '1rem' }} />
                {activeTab && (
                    <>
                        <RollToPaymentOptions activeTab={activeTab} />
                        <div style={{ marginTop: '1rem' }} />
                        <RollToPaymentProfit activeTab={activeTab} />
                    </>
                )}
            </div>
        </RollToSlideOut>
    );
};

export default RollToOptionsInterstate;
